/**
 * usePermissions hook
 *
 * Loads the current user's permission map from the backend
 * (GET /api/users/me/permissions) once Firebase auth is ready, then exposes
 * a `has(key)` helper for permission-aware UI gating.
 *
 * Refetches when the tab regains focus / becomes visible (throttled), so a
 * grant made in Admin → Permissions applies without signing out and back in.
 * Several components mount this hook on the same page (PermissionGate, the
 * header DashboardMenu, the launcher, the dashboards themselves), so the
 * request, the throttle window and the last good map are shared at module
 * level: one request per page, not one per instance.
 *
 * A failed fetch is reported through `error` and keeps the last good map — an
 * expired token, a 429 or a 500 must never be shown as "you don't have access".
 * The throttle covers failures too: within it, a focus refetch or a new mount
 * gets the last error back instead of asking again, so a 429 can't turn every
 * focus event into another request against the shared per-IP budget.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { auth } from '../config/firebase';
import { apiRequest } from '../services/api';

export type PermissionKey =
  | 'cgBoost.view'
  | 'admin.view'
  | 'financials.view'
  | 'financials.corrections'
  | 'sales.view'
  | 'adOps.view'
  | 'adOps.bundles.manage'
  | 'invoicing.view'
  | 'invoicing.manage'
  | 'invoicing.billing'
  | 'channelIntelligence.view'
  | 'channelIntelligence.manage'
  | 'takeovers.view'
  | 'takeovers.manage'
  | 'clientReports.manage'
  | 'adOps.campaignReports.manage'
  | 'tentpoleCalendar.manage'
  | 'mediaPlans.manage'
  | 'mcp.connect'
  | 'mcp.write';

export type PermissionMap = Partial<Record<PermissionKey, boolean>>;

interface UsePermissionsResult {
  loading: boolean;
  permissions: PermissionMap;
  /** Message of the last failed fetch; null once a fetch succeeds. */
  error: string | null;
  has: (key: PermissionKey) => boolean;
  /** Force a refetch now (ignores the focus throttle). */
  refresh: () => Promise<void>;
}

/**
 * A map fetched less than this long ago is reused by focus/visibility refetches
 * and new mounts; a fetch that failed less than this long ago is not retried.
 */
export const PERMISSIONS_REFETCH_THROTTLE_MS = 15_000;

let inFlight: Promise<PermissionMap> | null = null;
let cache: { uid: string; map: PermissionMap; fetchedAt: number } | null = null;
let lastFailure: { uid: string; error: unknown; failedAt: number } | null = null;

async function loadPermissionMap(uid: string, force: boolean): Promise<PermissionMap> {
  if (!force && cache && cache.uid === uid && Date.now() - cache.fetchedAt < PERMISSIONS_REFETCH_THROTTLE_MS) {
    return cache.map;
  }
  if (!force && lastFailure && lastFailure.uid === uid && Date.now() - lastFailure.failedAt < PERMISSIONS_REFETCH_THROTTLE_MS) {
    throw lastFailure.error;
  }
  if (!inFlight) {
    inFlight = apiRequest('/api/users/me/permissions')
      .then((body) => {
        const map = (body?.permissions ?? {}) as PermissionMap;
        cache = { uid, map, fetchedAt: Date.now() };
        lastFailure = null;
        return map;
      })
      .catch((error: unknown) => {
        lastFailure = { uid, error, failedAt: Date.now() };
        throw error;
      })
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/** Test-only: forget the shared cache, last failure and in-flight request. */
export function resetPermissionsCacheForTests(): void {
  inFlight = null;
  cache = null;
  lastFailure = null;
}

export function usePermissions(): UsePermissionsResult {
  const [permissions, setPermissions] = useState<PermissionMap>(() => cache?.map ?? {});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  const load = useCallback(async (force: boolean) => {
    const user = auth.currentUser;
    if (!user) {
      cache = null;
      if (mounted.current) {
        setPermissions({});
        setError(null);
        setLoading(false);
      }
      return;
    }
    try {
      const map = await loadPermissionMap(user.uid, force);
      if (mounted.current) {
        setPermissions(map);
        setError(null);
      }
    } catch (err: any) {
      console.warn('[usePermissions] Failed to load permissions:', err);
      if (mounted.current) {
        // Keep the last good map (if any) — a transport failure is not a revocation.
        if (cache && cache.uid === user.uid) setPermissions(cache.map);
        setError(err?.message || 'Failed to load permissions');
      }
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    const unsub = auth.onAuthStateChanged(() => {
      setLoading(true);
      void load(false);
    });
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void load(false);
    };
    const onFocus = () => {
      void load(false);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);
    return () => {
      mounted.current = false;
      unsub();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [load]);

  const has = useCallback(
    (key: PermissionKey) => permissions[key] === true,
    [permissions],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    await load(true);
  }, [load]);

  return { loading, permissions, error, has, refresh };
}
